import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import {
    Clock3,
    Instagram,
    MapPin,
    Navigation,
    Phone
} from 'lucide-react'
import { directionsUrl, whatsappUrl } from '@/lib/business'
import { BrandLogo } from '@/components/generals/brand-logo'
import { WhatsAppIcon } from '@/components/generals/icons'
import { services } from '@/constants/services'

import {
    address,
    contact,
    hours,
    navLinks,
    siteConfig
} from '@/constants/site'

const socialLinkClass = 'inline-flex size-10 items-center justify-center rounded-full border border-gold/30 text-gold-light transition-colors hover:border-gold hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
const footerLinkClass = 'text-sm text-ivory/70 transition-colors hover:text-gold-light focus-visible:outline-2 focus-visible:outline-gold'

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='plum-glow relative pb-24 text-ivory/80 md:pb-0'>
            <div className='gold-hairline h-px w-full' aria-hidden='true' />

            <div className='mx-auto max-w-7xl px-5 py-16 sm:px-8'>
                <div className='grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_0.8fr_1.3fr]'>
                    <div>
                        <BrandLogo />

                        <p className='gold-shine mt-4 inline-block px-1 pb-1 font-script text-3xl'>
                            {siteConfig.tagline}
                        </p>

                        <p className='mt-3 max-w-xs text-sm leading-relaxed text-ivory/70'>
                            A women's beauty parlour in New Gupta Colony, North Delhi for bridal makeups, makeovers, skin treatments, nail art, facial cleanup and hair care.
                        </p>

                        <div className='mt-6 flex items-center gap-3'>
                            <a href={contact.instagramUrl} target='_blank' rel='noopener noreferrer' aria-label='Follow us on Instagram' className={socialLinkClass}>
                                <Instagram className='size-4' aria-hidden='true' />
                            </a>

                            <a href={whatsappUrl()} target='_blank' rel='noopener noreferrer' aria-label='Chat with us on WhatsApp' className={socialLinkClass}>
                                <WhatsAppIcon className='size-4' />
                            </a>

                            <a href={contact.phoneHref} aria-label={`Call us on ${contact.phoneDisplay}`} className={socialLinkClass}>
                                <Phone className='size-4' aria-hidden='true' />
                            </a>

                            <a href={directionsUrl()} target='_blank' rel='noopener noreferrer' aria-label='Get directions on Google Maps' className={socialLinkClass}>
                                <Navigation className='size-4' aria-hidden='true' />
                            </a>
                        </div>
                    </div>

                    <div>
                        <h2 className='font-heading text-sm font-semibold tracking-[0.18em] text-gold-light'>
                            Our Services
                        </h2>

                        <ul className='mt-5 space-y-3'>
                            {services.map(service => (
                                <li key={service.slug}>
                                    <Link href={`/services/${service.slug}`} className={footerLinkClass}>
                                        {service.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h2 className='font-heading text-sm font-semibold tracking-[0.18em] text-gold-light'>
                            Explore
                        </h2>

                        <ul className='mt-5 space-y-3'>
                            {navLinks.map(link => (
                                <li key={link.href}>
                                    <Link href={link.href} className={footerLinkClass}>
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h2 className='font-heading text-sm font-semibold tracking-[0.18em] text-gold-light'>
                            Visit Us
                        </h2>

                        <ul className='mt-5 space-y-4 text-sm'>
                            <li className='flex gap-3'>
                                <MapPin className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                <address className='not-italic leading-relaxed text-ivory/75'>
                                    {address.lineOne}
                                    <br />
                                    {address.lineTwo}
                                    <br />
                                    <span className='text-ivory/55'>{address.landmark}</span>
                                </address>
                            </li>

                            <li className='flex gap-3'>
                                <Clock3 className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                <span className='leading-relaxed text-ivory/75'>
                                    {hours.days}
                                    <br />
                                    {hours.label}
                                </span>
                            </li>

                            <li className='flex gap-3'>
                                <Phone className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                <a href={contact.phoneHref} className='text-ivory/75 transition-colors hover:text-gold-light'>
                                    {contact.phoneDisplay}
                                </a>
                            </li>

                            <li className='flex gap-3'>
                                <Instagram className='mt-0.5 size-4 shrink-0 text-gold' aria-hidden='true' />
                                <a href={contact.instagramUrl} target='_blank' rel='noopener noreferrer' className='break-all text-ivory/75 transition-colors hover:text-gold-light'>
                                    {contact.instagramHandle}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs text-ivory/55 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All rights reserved.
                    </p>

                    <p>
                        Women's beauty parlour in New Gupta Colony, Kalyan Vihar, Delhi 110009
                    </p>
                </div>
            </div>
        </footer>
    )
}
